import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { firestore } from '../services/firebase';
import { PRODUCTS, PLANS, COURSES } from '../constants/entities';
import { sameEquivalent, withoutEquivalentLink } from './catalogEquivalentLinkUtils';
import { buildCatalogEquivalenceGroups } from './catalogEquivalenceGroup';

const TYPE_BY_COLLECTION = {
  [PRODUCTS]: 'product',
  [PLANS]: 'plan',
  [COURSES]: 'course',
};

const COLLECTION_BY_TYPE = {
  product: PRODUCTS,
  plan: PLANS,
  course: COURSES,
};

/** Inclui quem já aponta para este item (vínculos legados só em um sentido). */
export function mergeReverseEquivalentIds(catalogProducts, selfLink, equivalentIds = []) {
  const merged = [...equivalentIds];

  (catalogProducts || []).forEach((item) => {
    if (item.id === selfLink.id) return;

    const pointsToSelf = (item.equivalentIds || []).some((eq) => sameEquivalent(eq, selfLink));
    const alreadyListed = merged.some((eq) => sameEquivalent(eq, { id: item.id, type: item.type }));

    if (pointsToSelf && !alreadyListed) {
      merged.push({ id: item.id, type: item.type });
    }
  });

  return merged;
}

/**
 * Ao vincular A → B, grava B → A automaticamente (e remove o espelho ao
 * desvincular). Evita que o admin precise marcar os dois lados manualmente.
 */
export async function syncCatalogEquivalentIds({
  collectionName,
  docId,
  previousEquivalentIds = [],
  nextEquivalentIds = [],
}) {
  const selfType = TYPE_BY_COLLECTION[collectionName];
  if (!selfType || !docId) return;

  const selfLink = { id: docId, type: selfType };
  const prev = Array.isArray(previousEquivalentIds) ? previousEquivalentIds : [];
  const next = Array.isArray(nextEquivalentIds) ? nextEquivalentIds : [];

  const added = next.filter((eq) => !prev.some((item) => sameEquivalent(item, eq)));
  const removed = prev.filter((eq) => !next.some((item) => sameEquivalent(item, eq)));

  const touchTarget = async (target, shouldAdd) => {
    const targetCollection = COLLECTION_BY_TYPE[target.type];
    if (!targetCollection || sameEquivalent(target, selfLink)) return;

    const targetRef = doc(firestore, targetCollection, target.id);
    const snap = await getDoc(targetRef);
    if (!snap.exists()) return;

    const current = Array.isArray(snap.data().equivalentIds) ? snap.data().equivalentIds : [];
    const alreadyLinked = current.some((eq) => sameEquivalent(eq, selfLink));

    if (shouldAdd) {
      if (alreadyLinked) return;
      await updateDoc(targetRef, { equivalentIds: [...current, selfLink] });
      return;
    }

    if (!alreadyLinked) return;
    await updateDoc(targetRef, {
      equivalentIds: withoutEquivalentLink(current, selfLink),
    });
  };

  await Promise.all([
    ...added.map((target) => touchTarget(target, true)),
    ...removed.map((target) => touchTarget(target, false)),
  ]);
}

/**
 * Antes de excluir um produto/plano/curso, remove referências a ele em
 * equivalentIds de todos os outros docs (products, plans, courses).
 */
export async function purgeCatalogEquivalentRefs(deletedLink) {
  if (!deletedLink?.id || !deletedLink?.type) return 0;

  const deletedCollection = COLLECTION_BY_TYPE[deletedLink.type];
  let updatedCount = 0;

  for (const collectionName of [PRODUCTS, PLANS, COURSES]) {
    const snap = await getDocs(collection(firestore, collectionName));
    const updates = snap.docs
      .filter((docSnap) => {
        if (deletedCollection === collectionName && docSnap.id === deletedLink.id) {
          return false;
        }
        const current = docSnap.data().equivalentIds;
        return Array.isArray(current) && current.some((eq) => sameEquivalent(eq, deletedLink));
      })
      .map((docSnap) => {
        const current = docSnap.data().equivalentIds;
        return updateDoc(doc(firestore, collectionName, docSnap.id), {
          equivalentIds: withoutEquivalentLink(current, deletedLink),
        });
      });

    if (updates.length > 0) {
      await Promise.all(updates);
      updatedCount += updates.length;
    }
  }

  return updatedCount;
}

/**
 * Copia capa/categoria/ordem do representante excluído para o herdeiro e
 * re-vincula os sobreviventes do grupo (recíproco via syncCatalogEquivalentIds).
 */
export async function transferCatalogVitrineHeirship({
  deletedProduct,
  heirProduct,
  catalogProducts = [],
}) {
  if (!deletedProduct?.id || !heirProduct?.id) return;

  const { getGroupMembers } = buildCatalogEquivalenceGroups(catalogProducts);
  const otherSurvivors = getGroupMembers(deletedProduct.id).filter(
    (member) => member.id !== deletedProduct.id && member.id !== heirProduct.id
  );

  const heirCollection = COLLECTION_BY_TYPE[heirProduct.type];
  if (!heirCollection) return;

  const nextEquivalentIds = otherSurvivors.map((member) => ({
    id: member.id,
    type: member.type,
  }));

  const vitrinePayload = {
    showInCatalog: true,
    order: deletedProduct.order ?? 0,
    catalogImage: deletedProduct.catalogImage ?? null,
    productCategoryId: deletedProduct.productCategoryId ?? null,
    equivalentIds: nextEquivalentIds,
  };

  const previousEquivalentIds = Array.isArray(heirProduct.equivalentIds)
    ? heirProduct.equivalentIds
    : [];

  await updateDoc(doc(firestore, heirCollection, heirProduct.id), vitrinePayload);
  await syncCatalogEquivalentIds({
    collectionName: heirCollection,
    docId: heirProduct.id,
    previousEquivalentIds,
    nextEquivalentIds,
  });
}
